import React, { useState, useEffect } from "react";

import { Grid, Box, Modal, IconButton, MenuItem, SelectChangeEvent } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import * as yup from "yup";

import { useAppState } from "../../../../context/StateContext";
import { DifferentLocation } from "../../../../models/location";
import { LocationTypes } from "../../../../util/enums";
import Button, { Color } from "../../../Form/Button";
import Checkbox from "../../../Form/Checkbox";
import TextField from "../../../Form/Input";
import MapInput from "../../../Form/MapInput";
import Select from "../../../Form/Select";
import useStyles from "./styles";

interface Props {
  location?: DifferentLocation;
  handleClose?: (location?: DifferentLocation) => void;
  onDuplicateLocation?: (value: boolean) => void;
  open: boolean;
  view?: boolean;
  calledFrom?: string;
}

const LocationsModal = (props: Props) => {
  const { isMovingVersion, isSTVersion } = useAppState();
  const { open, handleClose, location, view, onDuplicateLocation, calledFrom } = props;

  const [name, setName] = useState<string>("");
  const [status, setStatus] = useState<string>("Active");
  const [longAddress, setLongAddress] = useState<string>("");
  const [suburb, setSuburb] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [postalcode, setPostalcode] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [isDelivery, setIsDelivery] = useState<boolean>(false);
  const [dcCode, setDcCode] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const { currentUser } = useAppState();

  useEffect(() => {
    if (isSubmitted) handleValidate();
  }, [isSubmitted, name, status, longAddress, suburb, state, postalcode, country]);

  useEffect(() => {
    if (location) {
      setName(location?.name ? location?.name : "");
      setStatus(location?.status ? location?.status : "Active");
      setLongAddress(location?.longAddress ? location?.longAddress : "");
      setSuburb(location?.suburb ? location?.suburb : "");
      setState(location?.state ? location?.state : "");
      setPostalcode(location?.postalcode ? location?.postalcode : "");
      setCountry(location?.country ? location?.country : "");
      setType(location?.type || "");
      setDcCode(location?.dcCode || "");
      if (location?.type === LocationTypes.DELIVERY) {
        setIsDelivery(true);
      }
    }
  }, [location, open]);

  useEffect(() => {
    if (open === false) {
      setName("");
      setStatus("Active");
      setLongAddress("");
      setSuburb("");
      setState("");
      setPostalcode("");
      setCountry("");
      setIsSubmitted(false);
      setErrors([]);
      setDcCode("");
      setIsDelivery(false);
      setType("");
    }
  }, [open]);

  const handleValidate = () => {
    try {
      const schema = yup.object().shape({
        name: yup.string().required(translate("youMustEnterAName")),
        longAddress: yup.string().required(translate("youMustEnterAnAddress")),
        suburb: yup.string().required(translate("youMustEnterASuburbTown")),
        state: yup.string().required(translate("youMustEnterAState")),
        postalcode: yup.string().required(translate("youMustEnterAPostalcode")),
        country: yup.string().required(translate("youMustEnterACountry")),
        isDelivery: yup.boolean(),
        dcCode: yup.string().when("isDelivery", {
          is: true,
          then: yup.string().required(translate("thisFieldIsRequired")),
        }),
      });

      schema.validateSync(
        {
          name,
          longAddress,
          suburb,
          state,
          postalcode,
          country,
          isDelivery,
          dcCode,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    if (handleValidate()) {
      const updatedLocation: DifferentLocation = {
        name,
        status,
        longAddress,
        suburb,
        state,
        postalcode,
        country,
        companyId: currentUser?.companyId,
        type,
        dcCode,
      };

      if (handleClose) {
        if (location && (location?.id || location?.uuid)) {
          handleClose({ ...location, ...updatedLocation });
        } else {
          handleClose({ uuid: uuid(), ...updatedLocation });
        }
      }
    }
  };

  const fieldHasError = (field: string) => !!errors?.find((error) => error.path === field);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;
  const renderTitle = () => {
    if (location && !view) {
      return (
        <span data-cy="locationModalTitle" className={classes.titleName}>
          {translate("editLocation")}
        </span>
      );
    }
    if (location && view) {
      return (
        <span data-cy="locationModalTitle" className={classes.titleName}>
          {translate("viewLocation")}
        </span>
      );
    }

    return (
      <span data-cy="locationModalTitle" className={classes.titleName}>
        {translate("createLocation")}
      </span>
    );
  };

  const renderSubTitle = () => {
    if (location && !view) {
      return <span className={classes.subTitle}>{translate("editLocationForCustomer")}</span>;
    }
    if (location && view) {
      return <span className={classes.subTitle}>{translate("viewLocationForCustomer")}</span>;
    }

    return <span className={classes.subTitle}>{translate("createNewLocationForCustomer")}</span>;
  };

  return (
    <Modal
      open={open}
      onClose={() => handleClose && handleClose()}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      <div>
        <div className={classes.paper}>
          <div className={classes.modalBody}>
            <Grid data-cy="locationModal" container direction="row">
              <Grid item xs={12} className={classes.header}>
                <div className={classes.locationModalTitle}>
                  <Box>{renderTitle()}</Box>
                  <Box>
                    <IconButton onClick={() => handleClose && handleClose()}>
                      <CloseIcon data-cy="locationCloseIcon" fontSize="medium" />
                    </IconButton>
                  </Box>
                </div>

                <Box>{renderSubTitle()}</Box>
              </Grid>

              <Grid container direction="row" style={{ padding: "1.5em" }} spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="location.name"
                    data-cy="locationName"
                    label={translate("name")}
                    onChange={(e) => setName(e.target.value)}
                    value={name}
                    error={fieldHasError("name")}
                    errorMessage={getErrorByField("name")}
                    disabled={view}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Select
                    name="location.status"
                    data-cy="locationStatus"
                    label={translate("status")}
                    onChange={(e: SelectChangeEvent<any>) => setStatus(e.target.value)}
                    value={status}
                    disabled={view}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </Grid>

                <Grid item xs={12}>
                  <MapInput
                    onChange={(location) => {
                      setLongAddress(location.longAddress || "");
                      setSuburb(location.suburb || "");
                      setCountry(location.country || "");
                      setPostalcode(location.postalcode || "");
                      setState(location.state || "");
                    }}
                    label={translate("address")}
                    onChangeInput={(value) => setLongAddress(value)}
                    inputValue={longAddress}
                    error={fieldHasError("longAddress")}
                    errorMessage={getErrorByField("longAddress")}
                    placeholder="Google Maps search"
                    data-cy="locationAddress"
                    disabled={view}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    name="location.country"
                    data-cy="locationCountry"
                    label={translate("country")}
                    onChange={(e) => setCountry(e.target.value)}
                    value={country}
                    error={fieldHasError("country")}
                    errorMessage={getErrorByField("country")}
                    disabled={view}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    name="location.state"
                    data-cy="locationState"
                    label={translate("state")}
                    onChange={(e) => setState(e.target.value)}
                    value={state}
                    error={fieldHasError("state")}
                    errorMessage={getErrorByField("state")}
                    disabled={view}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    name="location.suburb"
                    data-cy="locationSuburb"
                    label={translate("suburbTown")}
                    onChange={(e) => setSuburb(e.target.value)}
                    value={suburb}
                    error={fieldHasError("suburb")}
                    errorMessage={getErrorByField("suburb")}
                    disabled={view}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="location.postcode"
                    data-cy="locationPostalcode"
                    label={translate("postcode")}
                    onChange={(e) => setPostalcode(e.target.value)}
                    value={postalcode}
                    error={fieldHasError("postalcode")}
                    errorMessage={getErrorByField("postalcode")}
                    disabled={view}
                  />
                </Grid>
                {!isMovingVersion && !isSTVersion && (
                  <>
                    {calledFrom === "customer" && (
                      <Grid item xs={12}>
                        <Checkbox
                          disabled={view}
                          label={translate("thisIsADeliveryLocation")}
                          checked={isDelivery}
                          onChange={(_, checked) => {
                            setIsDelivery(checked);
                            if (type === LocationTypes.MAIN && onDuplicateLocation) {
                              if (checked) {
                                onDuplicateLocation(true);
                              } else {
                                onDuplicateLocation(false);
                              }
                            } else if (checked) {
                              setType(LocationTypes.DELIVERY);
                            } else {
                              setType("");
                            }
                          }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="location.dcCode"
                        data-cy="locationDcCode"
                        label={translate("dcCode")}
                        onChange={(e) => setDcCode(e.target.value)}
                        value={dcCode}
                        error={fieldHasError("dcCode")}
                        errorMessage={getErrorByField("dcCode")}
                        disabled={view}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid item xs={12} className={classes.locationButtonsContainer}>
                <Button
                  customColor={Color.CANCEL}
                  data-cy="locationCancelButton"
                  onClick={() => handleClose && handleClose()}
                >
                  {translate("cancel")}
                </Button>
                <Button
                  customColor={Color.SUCCESS}
                  data-cy="locationSaveButton"
                  onClick={() => handleSubmit()}
                  disabled={view}
                  className={classes.successButton}
                >
                  {location ? translate("save") : translate("create")}
                </Button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LocationsModal;
